import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useCallback } from 'react';

interface EngagementData {
  firstName: string;
  lastName: string;
  address: string;
  city: string;
  state: string;
  zipcode: string;
  phoneNumber: string;
  email: string;
  dob: string;
  gender: string;
  reminder: boolean;
  registrationStatus?: 'registered' | 'not_registered' | null;

  voterFullName: string;
  zipCode: string;
  internalNote: string;

  // New fields for reminders and pledge
  emailReminders: boolean;
  textReminders: boolean;
  reminder7Days: boolean;
  reminder1Day: boolean;
  reminderElectionDay: boolean;
  voterPledge: boolean;
  showConfirmation: boolean;
  pollingInfoOptIn: boolean;
  electionReminderOptIn: boolean;
}

interface EngagementState {
  currentStep: number;
  engagementData: EngagementData;
  isEngagementActive: boolean;
}

const initialEngagementState: EngagementState = {
  currentStep: 0,
  isEngagementActive: false,
  engagementData: {
    firstName: '',
    lastName: '',
    address: '',
    city: '',
    state: '',
    zipcode: '',
    phoneNumber: '',
    email: '',
    dob: '',
    gender: '',
    reminder: false,
    registrationStatus: null,
    voterFullName: '',
    zipCode: '',
    internalNote: '',
    // New fields for reminders and pledge
    emailReminders: false,
    textReminders: false,
    reminder7Days: false,
    reminder1Day: false,
    reminderElectionDay: false,
    voterPledge: false,
    showConfirmation: false,
    pollingInfoOptIn: false,
    electionReminderOptIn: false,
  },
};

const ENGAGEMENT_QUERY_KEY = ['engagement'];

export const useEngagementState = () => {
  const queryClient = useQueryClient();

  const { data = initialEngagementState } = useQuery({
    queryKey: ENGAGEMENT_QUERY_KEY,
    queryFn: () => initialEngagementState,
    staleTime: Infinity,
  });

  const setState = useCallback(
    (updater: (prev: EngagementState) => EngagementState) => {
      queryClient.setQueryData(ENGAGEMENT_QUERY_KEY, updater);
    },
    [queryClient]
  );

  const startEngagement = () =>
    setState(prev => ({
      ...prev,
      isEngagementActive: true,
      currentStep: 1,
    }));

  const endEngagement = () =>
    queryClient.setQueryData(ENGAGEMENT_QUERY_KEY, initialEngagementState);

  const nextStep = () =>
    setState(prev => ({
      ...prev,
      currentStep: Math.min(prev.currentStep + 1, 4),
    }));

  const previousStep = () =>
    setState(prev => ({
      ...prev,
      currentStep: Math.max(prev.currentStep - 1, 1),
    }));

  const setCurrentStep = (step: number) =>
    setState(prev => ({
      ...prev,
      currentStep: Math.min(Math.max(step, 1), 4),
    }));

  const updateEngagementData = (patch: Partial<EngagementData>) =>
    setState(prev => ({
      ...prev,
      engagementData: {
        ...prev.engagementData,
        ...patch,
      },
    }));

  return {
    ...data,
    startEngagement,
    endEngagement,
    nextStep,
    previousStep,
    setCurrentStep,
    updateEngagementData,
  };
};
